import React, { useState, useEffect, useRef } from 'react'

const WINNING_COMBINATIONS = [
  [0, 1, 2], [3, 4, 5], [6, 7, 8],
  [0, 3, 6], [1, 4, 7], [2, 5, 8],
  [0, 4, 8], [2, 4, 6]
]

const checkWinner = (squares) => {
  for (const combo of WINNING_COMBINATIONS) {
    const [a, b, c] = combo
    if (squares[a] && squares[a] === squares[b] && squares[a] === squares[c]) {
      return { winner: squares[a], line: combo }
    }
  }
  return null
}

const isBoardFull = (squares) => squares.every(s => s !== null)

const minimax = (squares, depth, isMaximizing) => {
  const result = checkWinner(squares)
  if (result) return result.winner === 'O' ? 10 - depth : depth - 10
  if (isBoardFull(squares)) return 0

  const empty = squares.map((s, i) => s === null ? i : null).filter(i => i !== null)
  
  if (isMaximizing) {
    let maxScore = -Infinity
    for (const idx of empty) {
      squares[idx] = 'O'
      maxScore = Math.max(maxScore, minimax(squares, depth + 1, false))
      squares[idx] = null
    }
    return maxScore
  } else {
    let minScore = Infinity
    for (const idx of empty) {
      squares[idx] = 'X'
      minScore = Math.min(minScore, minimax(squares, depth + 1, true))
      squares[idx] = null
    }
    return minScore
  }
}

const getBestMove = (squares) => {
  const empty = squares.map((s, i) => s === null ? i : null).filter(i => i !== null)
  if (empty.length === 0) return null
  
  let bestScore = -Infinity
  let bestMove = empty[0]

  for (const idx of empty) {
    const copy = [...squares]
    copy[idx] = 'O'
    const score = minimax(copy, 0, false)
    if (score > bestScore) { bestScore = score; bestMove = idx }
  }
  return bestMove
}

const getAIMove = (squares, diff) => {
  const empty = squares.map((s, i) => s === null ? i : null).filter(i => i !== null)
  if (empty.length === 0) return null
  if (diff === 'easy') return empty[Math.floor(Math.random() * empty.length)]
  if (diff === 'medium') {
    if (Math.random() < 0.5) return getBestMove(squares)
    return empty[Math.floor(Math.random() * empty.length)]
  }
  return getBestMove(squares)
}

export default function TicTacToe() {
  const [board, setBoard] = useState(Array(9).fill(null))
  const [isXNext, setIsXNext] = useState(true)
  const [gameMode, setGameMode] = useState(null)
  const [difficulty, setDifficulty] = useState('hard')
  const [winner, setWinner] = useState(null)
  const [winningLine, setWinningLine] = useState(null)
  const [isDraw, setIsDraw] = useState(false)
  const [scores, setScores] = useState({ X: 0, O: 0, draws: 0 })
  const [isAiThinking, setIsAiThinking] = useState(false)
  const [difficultySelected, setDifficultySelected] = useState(false)

  const aiTimeoutRef = useRef(null)
  const isPlayerMoving = useRef(false)

  const handleMove = (index) => {
    if (board[index] || winner || isDraw || isAiThinking) return

    const newBoard = [...board]
    const playerSymbol = gameMode === 'friend' ? (isXNext ? 'X' : 'O') : 'X'
    newBoard[index] = playerSymbol

    const result = checkWinner(newBoard)
    if (result) {
      setWinner(result.winner)
      setWinningLine(result.line)
      setScores(s => ({ ...s, [result.winner]: s[result.winner] + 1 }))
      setBoard(newBoard)
      return
    }
    if (isBoardFull(newBoard)) {
      setIsDraw(true)
      setScores(s => ({ ...s, draws: s.draws + 1 }))
      setBoard(newBoard)
      return
    }

    setBoard(newBoard)
    setIsXNext(!isXNext)
    isPlayerMoving.current = false
  }

  useEffect(() => {
    if (gameMode !== 'ai' || winner || isDraw) return
    if (isXNext) return
    if (isPlayerMoving.current) return

    isPlayerMoving.current = true
    if (aiTimeoutRef.current) clearTimeout(aiTimeoutRef.current)
    setIsAiThinking(true)

    aiTimeoutRef.current = setTimeout(() => {
      const move = getAIMove(board, difficulty)
      if (move === null) { setIsAiThinking(false); return }

      const newBoard = [...board]
      newBoard[move] = 'O'

      const result = checkWinner(newBoard)
      if (result) {
        setWinner(result.winner)
        setWinningLine(result.line)
        setScores(s => ({ ...s, [result.winner]: s[result.winner] + 1 }))
        setBoard(newBoard)
        setIsAiThinking(false)
        return
      }
      if (isBoardFull(newBoard)) {
        setIsDraw(true)
        setScores(s => ({ ...s, draws: s.draws + 1 }))
        setBoard(newBoard)
        setIsAiThinking(false)
        return
      }

      setBoard(newBoard)
      setIsXNext(true)
      setIsAiThinking(false)
    }, 300)

    return () => clearTimeout(aiTimeoutRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isXNext, winner, isDraw, board, difficulty, gameMode])

  useEffect(() => {
    return () => { if (aiTimeoutRef.current) clearTimeout(aiTimeoutRef.current) }
  }, [])

  const resetGame = () => {
    setBoard(Array(9).fill(null))
    setIsXNext(true)
    setWinner(null)
    setWinningLine(null)
    setIsDraw(false)
  }

  const resetScores = () => {
    setScores({ X: 0, O: 0, draws: 0 })
    setDifficultySelected(false)
    resetGame()
  }

  const getCellClass = (index) => {
    const base = 'w-full h-full flex items-center justify-center font-black transition-all duration-200'
    if (winningLine && winningLine.includes(index)) return `${base} ${winner === 'O' ? 'animate-pulse' : ''}`
    return base
  }

  return (
    <div className="flex flex-col items-center" style={{ maxWidth: '400px', margin: '0 auto', width: '100%', padding: '0.5rem' }}>
      {!gameMode ? (
        <div className="flex flex-col gap-6 w-full">
          <p className="text-center text-slate-400 mb-2 font-mono">Choose game mode:</p>
          <div className="flex gap-4 justify-center">
            <button onClick={() => setGameMode('ai')} className="px-6 py-3 rounded-lg font-bold transition-all duration-200" style={{
              background: 'transparent', border: '2px solid #00d9ff', color: '#00d9ff', boxShadow: '0 0 10px rgba(0,217,255,0.3)'
            }} onMouseEnter={(e) => { e.target.style.background = 'rgba(0,217,255,0.1)'; e.target.style.boxShadow = '0 0 20px rgba(0,217,255,0.6)' }}
              onMouseLeave={(e) => { e.target.style.background = 'transparent'; e.target.style.boxShadow = '0 0 10px rgba(0,217,255,0.3)' }}>VS AI</button>
            <button onClick={() => setGameMode('friend')} className="px-6 py-3 rounded-lg font-bold transition-all duration-200" style={{
              background: 'transparent', border: '2px solid #ff006e', color: '#ff006e', boxShadow: '0 0 10px rgba(255,0,110,0.3)'
            }} onMouseEnter={(e) => { e.target.style.background = 'rgba(255,0,110,0.1)'; e.target.style.boxShadow = '0 0 20px rgba(255,0,110,0.6)' }}
              onMouseLeave={(e) => { e.target.style.background = 'transparent'; e.target.style.boxShadow = '0 0 10px rgba(255,0,110,0.3)' }}>VS Friend</button>
          </div>
        </div>
      ) : gameMode === 'ai' && !difficultySelected ? (
        <div className="flex flex-col gap-6 w-full">
          <p className="text-center text-slate-400 mb-2 font-mono">Select difficulty:</p>
          <div className="flex gap-3 justify-center">
            {['easy', 'medium', 'hard'].map((diff) => (
              <button key={diff} onClick={() => { setDifficulty(diff); setDifficultySelected(true) }} className="px-4 py-2 rounded-lg font-bold transition-all duration-200 capitalize" style={{
                background: difficulty === diff ? 'rgba(0,217,255,0.2)' : 'transparent',
                border: `2px solid ${difficulty === diff ? '#00d9ff' : 'rgba(0,217,255,0.3)'}`,
                color: '#00d9ff', boxShadow: difficulty === diff ? '0 0 15px rgba(0,217,255,0.5)' : 'none'
              }}>{diff}</button>
            ))}
          </div>
        </div>
      ) : (
        <>
          <div className="flex gap-6 mb-4 font-mono" style={{ fontSize: 'clamp(14px, 4vw, 18px)' }}>
            <span style={{ color: '#ff006e', textShadow: '0 0 10px #ff006e' }}>X: {scores.X}</span>
            <span style={{ color: '#94a3b8' }}>Draws: {scores.draws}</span>
            <span style={{ color: '#00d9ff', textShadow: '0 0 10px #00d9ff' }}>O: {scores.O}</span>
          </div>

          {isAiThinking && (
            <div className="mb-3 text-sm font-mono" style={{ color: '#d000ff', textShadow: '0 0 8px #d000ff' }}>AI is thinking...</div>
          )}

          <div className="relative" style={{
            width: 'min(360px, 85vw)', height: 'min(360px, 85vw)', display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)', gridTemplateRows: 'repeat(3, 1fr)',
            border: '2px solid rgba(0,217,255,0.4)', borderRadius: '8px', overflow: 'hidden', background: 'rgba(10,14,39,0.8)',
            touchAction: 'none',
          }}>
            {board.map((cell, index) => (
              <div key={index} onClick={() => handleMove(index)} className={getCellClass(index)} style={{
                border: index % 3 !== 2 ? '2px solid rgba(0,217,255,0.4)' : 'none',
                borderBottom: index < 6 ? '2px solid rgba(0,217,255,0.4)' : 'none',
                cursor: (cell || winner || isDraw || isAiThinking) ? 'default' : 'pointer',
                background: winningLine?.includes(index) ? (winner === 'O' ? 'rgba(0,217,255,0.1)' : 'rgba(255,0,110,0.1)') : 'transparent',
                transition: 'background 0.2s',
              }} onMouseEnter={(e) => { if (!cell && !winner && !isDraw && !isAiThinking) e.currentTarget.style.background = 'rgba(0,217,255,0.05)' }}
                onMouseLeave={(e) => { if (!winningLine) e.currentTarget.style.background = 'transparent' }}>
                {cell && <span className="animate-scale-in" style={{ color: cell === 'X' ? '#ff006e' : '#00d9ff', textShadow: `0 0 20px ${cell === 'X' ? '#ff006e' : '#00d9ff'}`, fontSize: 'clamp(32px, 10vw, 56px)', animation: 'scaleIn 0.3s ease-out forwards' }}>{cell}</span>}
              </div>
            ))}
          </div>

          {(winner || isDraw) && (
            <div className="mt-6 p-4 rounded-lg text-center animate-fade-in" style={{
              background: 'rgba(10,14,39,0.95)',
              border: `1px solid ${winner === 'X' ? '#ff006e' : winner === 'O' ? '#00d9ff' : '#d000ff'}`,
              boxShadow: `0 0 20px ${winner === 'X' ? 'rgba(255,0,110,0.3)' : winner === 'O' ? 'rgba(0,217,255,0.3)' : 'rgba(208,0,255,0.3)'}`,
            }}>
              <p className="text-2xl font-bold mb-2" style={{
                color: winner === 'X' ? '#ff006e' : winner === 'O' ? '#00d9ff' : '#d000ff',
                textShadow: `0 0 15px ${winner === 'X' ? '#ff006e' : winner === 'O' ? '#00d9ff' : '#d000ff'}`,
              }}>{winner === 'X' ? 'X WINS!' : winner === 'O' ? 'O WINS!' : 'IT\'S A DRAW!'}</p>
              <button onClick={resetGame} className="px-6 py-2 rounded-lg font-bold transition-all duration-200" style={{
                background: 'transparent', border: `2px solid ${winner === 'X' ? '#ff006e' : winner === 'O' ? '#00d9ff' : '#d000ff'}`,
                color: winner === 'X' ? '#ff006e' : winner === 'O' ? '#00d9ff' : '#d000ff',
              }} onMouseEnter={(e) => { e.target.style.background = winner === 'X' ? 'rgba(255,0,110,0.1)' : winner === 'O' ? 'rgba(0,217,255,0.1)' : 'rgba(208,0,255,0.1)' }}
                onMouseLeave={(e) => { e.target.style.background = 'transparent' }}>New Game</button>
            </div>
          )}

          <div className="flex gap-4 mt-4">
            <button onClick={resetGame} className="px-4 py-2 rounded-lg font-mono text-sm transition-all duration-200" style={{
              background: 'transparent', border: '2px solid rgba(0,217,255,0.5)', color: '#94a3b8',
            }}>Reset Board</button>
            <button onClick={resetScores} className="px-4 py-2 rounded-lg font-mono text-sm transition-all duration-200" style={{
              background: 'transparent', border: '2px solid rgba(255,0,110,0.5)', color: '#94a3b8',
            }}>Reset Scores</button>
          </div>
          
          <button onClick={() => { setGameMode(null); setDifficultySelected(false); resetGame() }} className="mt-4 text-sm text-slate-500 hover:text-slate-300 transition-colors">← Back to mode selection</button>
        </>
      )}

      <style>{`
        @keyframes scaleIn { from { transform: scale(0); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        .animate-scale-in { animation: scaleIn 0.3s ease-out forwards; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
      `}</style>
    </div>
  )
}
